/**
 * O que faz: Página de Agenda/Calendário — visualização em dia, semana e mês,
 * com lista agrupada por dia, timeline, métricas do período e CRUD de agendamentos.
 * Onde usa: Rota /agenda (via pages/AgendaPage.tsx).
 * Conexões: Hooks useFetchAgendamentos/useDeleteAgendamento/useUpdateAgendamento,
 * RegistroAgendamentoModal, AgendamentoCard, TimelineDayView e utils de status.
 * Tabelas: Lê/escreve na tabela 'agendamentos' (somente via hooks — nenhuma query direta aqui).
 * Autor: IA (Kilo)
 * Data: 22/07/2026
 */
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Clock, CalendarDays, CalendarClock, CheckCircle2, BadgeCheck } from 'lucide-react';
import { RegistroAgendamentoModal } from '@/components/agendamentos/RegistroAgendamentoModal';
import { useFetchAgendamentos, useDeleteAgendamento, useUpdateAgendamento, AgendamentoFromDatabase } from '@/hooks/useAgendamentosData';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, startOfMonth, endOfMonth, isSameMonth, isSameDay, isToday, isTomorrow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { AgendamentoStatus } from '@/constants/agendamentos';
import {
  FORMAS_PAGAMENTO_AGENDAMENTO,
  ehFormaPagamentoValida,
  montarCamposPagamentoAgendamento,
} from '@/constants/pagamentoAgendamento';
import { getStatusClasses, getStatusText } from '@/utils/agendamentoStatus';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from "@/components/ui/skeleton";
import { TimelineDayView } from '@/components/calendar/TimelineDayView';
import { AgendamentoCard } from '@/components/calendar/AgendamentoCard';
import { generateTimeSlots, SLOT_HEIGHT_PX, START_HOUR } from '@/utils/timelineUtils';
import { cn } from '@/lib/utils';

type MonthViewMode = 'calendar' | 'list' | 'grid';
type DisplayMode = 'list' | 'timeline';

export const CalendarPage = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>('week');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('list');
  const [monthViewMode, setMonthViewMode] = useState<MonthViewMode>('calendar');
  const [isAgendamentoModalOpen, setIsAgendamentoModalOpen] = useState(false);
  const [agendamentoParaEditar, setAgendamentoParaEditar] = useState<AgendamentoFromDatabase | null>(null);

  // Estado do diálogo de confirmação de exclusão
  const [agendamentoParaDeletarId, setAgendamentoParaDeletarId] = useState<string | null>(null);

  // Estado do diálogo de confirmação de MUDANÇA DE STATUS
  const [statusUpdateConfirmation, setStatusUpdateConfirmation] = useState<{
    agendamento: AgendamentoFromDatabase;
    newStatus: AgendamentoStatus;
  } | null>(null);

  // Valor confirmado ao marcar como pago
  const [valorConfirmado, setValorConfirmado] = useState<string>('');
  const [formaPagamentoConfirmada, setFormaPagamentoConfirmada] = useState('');
  const [parcelasConfirmadas, setParcelasConfirmadas] = useState('1');

  // Buscar agendamentos e instanciar as mutações de delete e update
  const {
    data: agendamentos = [],
    isLoading: loadingAgendamentos
  } = useFetchAgendamentos();
  const deleteAgendamentoMutation = useDeleteAgendamento();
  const updateAgendamentoMutation = useUpdateAgendamento();

  // O cliente vem junto do agendamento; o mapa evita baixar todos os leads
  // e elimina buscas repetidas nos cards.
  const clientesPorId = useMemo(() => new Map(
    agendamentos
      .filter(agendamento => agendamento.cliente)
      .map(agendamento => [agendamento.cliente_id, agendamento.cliente!]),
  ), [agendamentos]);

  // Navegar entre períodos conforme a visualização ativa
  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    switch (viewMode) {
      case 'day':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
        break;
    }
    setCurrentDate(newDate);
  };

  // Título do período exibido no cabeçalho dos controles
  const formatDateHeader = () => {
    const options: Intl.DateTimeFormatOptions = {};
    switch (viewMode) {
      case 'day':
        options.weekday = 'long';
        options.day = 'numeric';
        options.month = 'long';
        options.year = 'numeric';
        break;
      case 'week': {
        const startWeek = startOfWeek(currentDate, { locale: ptBR });
        const endWeek = endOfWeek(currentDate, { locale: ptBR });
        return `${format(startWeek, 'dd', { locale: ptBR })} de ${format(startWeek, 'MMMM', { locale: ptBR })} a ${format(endWeek, 'dd', { locale: ptBR })} de ${format(endWeek, 'MMMM yyyy', { locale: ptBR })}`;
      }
      case 'month':
        options.month = 'long';
        options.year = 'numeric';
        break;
    }
    return currentDate.toLocaleDateString('pt-BR', options);
  };

  // Nome do cliente pelo ID (fallback: telefone, pois muitos leads chegam sem nome)
  const getClienteNome = (clienteId: string) => {
    const cliente = clientesPorId.get(clienteId);
    return cliente?.nome || cliente?.telefone || 'Cliente sem nome';
  };

  // Abrir modal de edição
  const handleEditAgendamento = (agendamento: AgendamentoFromDatabase) => {
    setAgendamentoParaEditar(agendamento);
    setIsAgendamentoModalOpen(true);
  };

  // Abrir modal de criação
  const handleNovoAgendamento = () => {
    setAgendamentoParaEditar(null);
    setIsAgendamentoModalOpen(true);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setAgendamentoParaEditar(null);
    setIsAgendamentoModalOpen(false);
  };

  // Controle do diálogo de exclusão
  const handleOpenDeleteDialog = (id: string) => {
    setAgendamentoParaDeletarId(id);
  };
  const handleConfirmDelete = () => {
    if (agendamentoParaDeletarId) {
      deleteAgendamentoMutation.mutate(agendamentoParaDeletarId);
      setAgendamentoParaDeletarId(null);
    }
  };

  // Controle da mudança de status e seu diálogo de confirmação
  const handleStatusChange = (agendamento: AgendamentoFromDatabase, newStatus: AgendamentoStatus) => {
    // Ao marcar como 'pago', pré-preenche o valor atual do agendamento
    // para facilitar a confirmação ou o ajuste.
    if (newStatus === AgendamentoStatus.PAGO) {
      setValorConfirmado(String(agendamento.valor || ''));
      setFormaPagamentoConfirmada(agendamento.forma_pagamento || '');
      setParcelasConfirmadas(String(agendamento.parcelas || 1));
    }
    setStatusUpdateConfirmation({ agendamento, newStatus });
  };
  const handleConfirmStatusUpdate = () => {
    if (statusUpdateConfirmation) {
      if (statusUpdateConfirmation.newStatus === AgendamentoStatus.PAGO && !ehFormaPagamentoValida(formaPagamentoConfirmada)) {
        return;
      }

      const camposPagamento = montarCamposPagamentoAgendamento(statusUpdateConfirmation.newStatus, {
        formaPagamento: formaPagamentoConfirmada,
        parcelas: parcelasConfirmadas,
        pagoEm: new Date(),
      });

      const payload: {
        id: string;
        status: AgendamentoStatus;
        valor?: number;
        forma_pagamento?: string | null;
        parcelas?: number | null;
        pago_em?: string | null;
      } = {
        id: statusUpdateConfirmation.agendamento.id,
        status: statusUpdateConfirmation.newStatus,
        ...camposPagamento,
      };

      if (statusUpdateConfirmation.newStatus === AgendamentoStatus.PAGO) {
        payload.valor = Number(valorConfirmado) || 0;
      }
      updateAgendamentoMutation.mutate(payload);
      setStatusUpdateConfirmation(null);
      setValorConfirmado('');
      setFormaPagamentoConfirmada('');
      setParcelasConfirmadas('1');
    }
  };

  // Descrição do diálogo de confirmação de status
  const getStatusChangeDescription = () => {
    if (!statusUpdateConfirmation) return '';
    const oldStatusLabel = getStatusText(statusUpdateConfirmation.agendamento.status).toLowerCase();
    const newStatusLabel = getStatusText(statusUpdateConfirmation.newStatus).toLowerCase();
    return `Você tem certeza que deseja alterar o status de "${oldStatusLabel}" para "${newStatusLabel}"?`;
  };

  // Abrir chat do cliente
  const handleOpenChat = (clienteId: string) => {
    navigate(`/chat?leadId=${clienteId}`);
  };

  // Ir direto para a visão de um dia específico (usado no "+N mais" do calendário mensal)
  const handleGoToDay = (day: Date) => {
    setCurrentDate(day);
    setViewMode('day');
  };

  // Agendamentos do período visível, memoizados para não refiltrar a cada render
  const agendamentosFiltrados = useMemo(() => {
    return agendamentos.filter(agendamento => {
      const dataAgendamento = new Date(agendamento.data_inicio);
      switch (viewMode) {
        case 'day':
          return isSameDay(dataAgendamento, currentDate);
        case 'week': {
          const startWeek = startOfWeek(currentDate, { locale: ptBR });
          const endWeek = endOfWeek(currentDate, { locale: ptBR });
          return dataAgendamento >= startWeek && dataAgendamento <= endWeek;
        }
        case 'month':
          return isSameMonth(dataAgendamento, currentDate);
        default:
          return true;
      }
    });
  }, [agendamentos, currentDate, viewMode]);

  // Ordena cronologicamente: essencial para a leitura da lista e dos grupos por dia
  const agendamentosOrdenados = useMemo(
    () => [...agendamentosFiltrados].sort(
      (a, b) => new Date(a.data_inicio).getTime() - new Date(b.data_inicio).getTime(),
    ),
    [agendamentosFiltrados],
  );

  // Agrupa por dia para a lista: cabeçalhos "Hoje"/"Amanhã"/data facilitam
  // a varredura visual na visão semanal e mensal.
  const gruposPorDia = useMemo(() => {
    const grupos = new Map<string, { data: Date; itens: AgendamentoFromDatabase[] }>();
    agendamentosOrdenados.forEach(agendamento => {
      const data = new Date(agendamento.data_inicio);
      const chave = format(data, 'yyyy-MM-dd');
      const grupo = grupos.get(chave);
      if (grupo) {
        grupo.itens.push(agendamento);
      } else {
        grupos.set(chave, { data, itens: [agendamento] });
      }
    });
    return Array.from(grupos.values());
  }, [agendamentosOrdenados]);

  // Métricas calculadas sobre o PERÍODO VISÍVEL (não o histórico completo):
  // alinha o número exibido com o que o usuário está vendo na tela.
  const metricas = useMemo(() => ({
    total: agendamentosFiltrados.length,
    agendados: agendamentosFiltrados.filter(a => a.status === AgendamentoStatus.AGENDADO).length,
    confirmados: agendamentosFiltrados.filter(a => a.status === AgendamentoStatus.CONFIRMADO).length,
    realizados: agendamentosFiltrados.filter(a => a.status === AgendamentoStatus.REALIZADO).length,
  }), [agendamentosFiltrados]);

  // Rótulo do cabeçalho de cada grupo de dia na lista
  const formatarRotuloDia = (data: Date) => {
    if (isToday(data)) return 'Hoje';
    if (isTomorrow(data)) return 'Amanhã';
    return format(data, "EEEE, dd 'de' MMMM", { locale: ptBR });
  };

  // Renderiza a grade do calendário mensal
  const renderCalendarGrid = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart, { locale: ptBR });
    const endDate = endOfWeek(monthEnd, { locale: ptBR });
    const days = eachDayOfInterval({ start: startDate, end: endDate });

    return (
      <div className="grid grid-cols-7 gap-1.5">
        {/* Cabeçalho dos dias da semana */}
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
          <div key={day} className="pb-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-400">
            {day}
          </div>
        ))}

        {/* Células dos dias */}
        {days.map(day => {
          const dayAgendamentos = agendamentos.filter(agendamento =>
            isSameDay(new Date(agendamento.data_inicio), day),
          );
          const foraDoMes = !isSameMonth(day, currentDate);
          const ehHoje = isToday(day);

          return (
            <div
              key={day.toISOString()}
              onClick={() => handleGoToDay(day)}
              className={cn(
                'min-h-[96px] cursor-pointer rounded-xl border p-1.5 transition-colors md:min-h-[112px]',
                foraDoMes
                  ? 'border-transparent bg-gray-50/60 text-gray-400'
                  : 'border-gray-100 bg-white hover:border-green-200 hover:bg-green-50/30',
                ehHoje && 'border-green-300 bg-green-50/40',
              )}
              title={`Ver ${format(day, "dd 'de' MMMM", { locale: ptBR })}`}
            >
              <div className="mb-1 flex justify-center">
                {/* Número do dia; o dia atual ganha destaque na cor da marca
                    para orientar o usuário no calendário */}
                <span
                  className={cn(
                    'inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium',
                    ehHoje ? 'bg-green-700 font-semibold text-white' : 'text-gray-700',
                    foraDoMes && !ehHoje && 'text-gray-400',
                  )}
                >
                  {format(day, 'd')}
                </span>
              </div>

              <div className="space-y-1">
                {dayAgendamentos.slice(0, 2).map(agendamento => {
                  const classes = getStatusClasses(agendamento.status);
                  return (
                    <div
                      key={agendamento.id}
                      onClick={e => {
                        // Impede que o clique no agendamento dispare a navegação da célula
                        e.stopPropagation();
                        handleEditAgendamento(agendamento);
                      }}
                      className={cn(
                        'cursor-pointer truncate rounded-md px-1.5 py-0.5 text-[11px] font-medium transition-opacity hover:opacity-80',
                        classes.tagBg,
                        classes.tagText,
                      )}
                    >
                      {format(new Date(agendamento.data_inicio), 'HH:mm')} · {getClienteNome(agendamento.cliente_id)}
                    </div>
                  );
                })}
                {dayAgendamentos.length > 2 && (
                  <div className="px-1 text-[11px] font-medium text-green-700">
                    +{dayAgendamentos.length - 2} mais
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Timeline da semana inteira (coluna de horários fixa + 7 colunas de dias)
  const renderWeekTimeline = () => {
    const start = startOfWeek(currentDate, { locale: ptBR });
    const end = endOfWeek(currentDate, { locale: ptBR });
    const weekDays = eachDayOfInterval({ start, end });
    const timeSlots = generateTimeSlots(START_HOUR);

    return (
      <div className="flex h-full">
        {/* Coluna de horários fixa à esquerda */}
        <div className="sticky left-0 z-20 w-16 flex-shrink-0 border-r border-gray-200 bg-white">
          <div className="h-16 border-b border-gray-200" />
          {timeSlots.map(slot => {
            if (slot.endsWith(':00')) {
              return (
                <div
                  key={`time-${slot}`}
                  className="relative pr-2 text-right text-xs text-gray-500"
                  style={{ height: `${SLOT_HEIGHT_PX * 2}px` }}
                >
                  <span className="absolute -top-1.5 right-2">{slot}</span>
                </div>
              );
            }
            return null;
          })}
        </div>

        {/* Colunas dos dias */}
        <div className="grid flex-1 grid-cols-7">
          {weekDays.map(day => {
            const ehHoje = isToday(day);
            return (
              <div key={day.toISOString()} className="min-w-[200px] border-r border-gray-200">
                {/* Cabeçalho do dia, fixo no topo ao rolar; hoje destacado na cor da marca */}
                <div
                  className={cn(
                    'sticky top-0 z-10 flex h-16 flex-col justify-center border-b p-2 text-center',
                    ehHoje ? 'border-green-200 bg-green-50/60' : 'border-gray-200 bg-white',
                  )}
                >
                  <p className={cn('text-sm font-semibold capitalize', ehHoje ? 'text-green-700' : 'text-gray-700')}>
                    {format(day, 'EEE', { locale: ptBR })}
                  </p>
                  <p className={cn('text-2xl font-bold', ehHoje ? 'text-green-700' : 'text-gray-900')}>
                    {format(day, 'd')}
                  </p>
                </div>

                <TimelineDayView
                  agendamentosDoDia={agendamentos.filter(ag => isSameDay(new Date(ag.data_inicio), day))}
                  getClienteNome={getClienteNome}
                  getStatusClasses={getStatusClasses}
                  onEditAgendamento={handleEditAgendamento}
                  showTimeColumn={false}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // A timeline precisa de altura limitada com scroll próprio (comportamento de calendário);
  // já a lista flui naturalmente com o scroll da página, sem container interno.
  const emModoTimeline =
    (viewMode === 'day' || viewMode === 'week') && displayMode === 'timeline';

  return (
    <div className="flex flex-col gap-4 md:gap-5">
      {/* Header da página */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Agenda</h2>
          <p className="mt-1 text-gray-600">
            Gerencie os agendamentos da sua clínica
          </p>
        </div>
        <button
          onClick={handleNovoAgendamento}
          className="flex items-center gap-2 rounded-xl bg-green-700 px-4 py-2 text-white shadow-sm transition-colors hover:bg-green-600"
        >
          <Plus size={20} />
          Novo Agendamento
        </button>
      </div>

      {/* Métricas do período em fluxo normal (sem sobreposição):
          antes ficavam fixas no rodapé cobrindo o fim da lista */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          { rotulo: 'Total no período', valor: metricas.total, icone: CalendarDays, corIcone: 'text-blue-600', fundoIcone: 'bg-blue-50' },
          { rotulo: 'Agendados', valor: metricas.agendados, icone: CalendarClock, corIcone: 'text-yellow-600', fundoIcone: 'bg-yellow-50' },
          { rotulo: 'Confirmados', valor: metricas.confirmados, icone: CheckCircle2, corIcone: 'text-green-600', fundoIcone: 'bg-green-50' },
          { rotulo: 'Realizados', valor: metricas.realizados, icone: BadgeCheck, corIcone: 'text-purple-600', fundoIcone: 'bg-purple-50' },
        ].map(({ rotulo, valor, icone: Icone, corIcone, fundoIcone }) => (
          <div
            key={rotulo}
            className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-card"
          >
            <div className={cn('flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl', fundoIcone)}>
              <Icone size={18} className={corIcone} />
            </div>
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-gray-500">{rotulo}</p>
              {loadingAgendamentos ? (
                <Skeleton className="mt-1 h-6 w-10" />
              ) : (
                <p className="text-xl font-bold text-gray-900">{valor}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Controles do calendário */}
      <div className="rounded-2xl border border-gray-200 bg-white p-3 md:p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Navegação de data */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateDate('prev')}
              className="rounded-lg p-2 transition-colors hover:bg-gray-100"
              title="Período anterior"
            >
              <ChevronLeft size={20} />
            </button>

            <h3 className="min-w-[220px] text-center text-base font-semibold capitalize text-gray-900 md:min-w-[300px] md:text-lg">
              {formatDateHeader()}
            </h3>

            <button
              onClick={() => navigateDate('next')}
              className="rounded-lg p-2 transition-colors hover:bg-gray-100"
              title="Próximo período"
            >
              <ChevronRight size={20} />
            </button>

            <button
              onClick={() => setCurrentDate(new Date())}
              className="rounded-xl border border-gray-300 px-3 py-1.5 text-sm transition-colors hover:bg-gray-50"
            >
              Hoje
            </button>
          </div>

          {/* Controles de Visualização */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Seletor Lista/Timeline para Dia e Semana */}
            {(viewMode === 'day' || viewMode === 'week') && (
              <div className="flex rounded-xl bg-gray-100 p-1">
                {(['list', 'timeline'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => setDisplayMode(mode)}
                    className={cn(
                      'rounded-lg px-3 py-1 text-sm font-medium transition-colors',
                      displayMode === mode
                        ? 'bg-white text-green-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900',
                    )}
                  >
                    {mode === 'list' ? 'Lista' : 'Timeline'}
                  </button>
                ))}
              </div>
            )}

            {/* Seletor Dia/Semana/Mês */}
            <div className="flex rounded-xl bg-gray-100 p-1">
              {(['day', 'week', 'month'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => {
                    setViewMode(mode);
                    // Timeline não existe para mês: volta para lista ao trocar
                    if (mode === 'month') setDisplayMode('list');
                  }}
                  className={cn(
                    'rounded-lg px-3 py-1 text-sm font-medium transition-colors',
                    viewMode === mode
                      ? 'bg-white text-green-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900',
                  )}
                >
                  {mode === 'day' ? 'Dia' : mode === 'week' ? 'Semana' : 'Mês'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sub-modos da visualização mensal */}
        {viewMode === 'month' && (
          <div className="mt-3 flex items-center gap-2 border-t border-gray-200 pt-3">
            <span className="mr-1 text-sm text-gray-600">Visualização:</span>
            {(['calendar', 'list', 'grid'] as MonthViewMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => setMonthViewMode(mode)}
                className={cn(
                  'rounded-lg px-3 py-1 text-sm transition-colors',
                  monthViewMode === mode
                    ? 'bg-green-100 font-medium text-green-700'
                    : 'text-gray-600 hover:bg-gray-100',
                )}
              >
                {mode === 'calendar' ? 'Calendário' : mode === 'list' ? 'Lista' : 'Grade'}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Área principal */}
      {loadingAgendamentos ? (
        // Skeleton no mesmo formato do conteúdo evita "pulo" de layout ao carregar
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-36 rounded-2xl" />
          ))}
        </div>
      ) : emModoTimeline ? (
        // Timeline: container com altura própria e scroll interno (padrão de calendários)
        <div className="h-[70vh] min-h-[440px] overflow-auto rounded-2xl border border-gray-200 bg-white">
          {viewMode === 'day' ? (
            <div className="h-full p-4">
              <TimelineDayView
                agendamentosDoDia={agendamentosFiltrados}
                getClienteNome={getClienteNome}
                getStatusClasses={getStatusClasses}
                onEditAgendamento={handleEditAgendamento}
              />
            </div>
          ) : (
            renderWeekTimeline()
          )}
        </div>
      ) : viewMode === 'month' && monthViewMode === 'calendar' ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 md:p-6">
          {renderCalendarGrid()}
        </div>
      ) : (
        // Lista: sem container rolável — o conteúdo cresce e o scroll é o da página
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Agendamentos {viewMode === 'day' ? 'do dia' : viewMode === 'week' ? 'da semana' : 'do mês'}
            </h3>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
              {agendamentosOrdenados.length} agendamento(s)
            </span>
          </div>

          {gruposPorDia.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
                <Clock className="h-7 w-7 text-green-700" />
              </div>
              <h3 className="mb-1 text-lg font-semibold text-gray-900">Nenhum agendamento encontrado</h3>
              <p className="mb-5 text-sm text-gray-500">
                Não há agendamentos para o período selecionado.
              </p>
              <button
                onClick={handleNovoAgendamento}
                className="rounded-xl bg-green-700 px-4 py-2 text-white transition-colors hover:bg-green-600"
              >
                Criar primeiro agendamento
              </button>
            </div>
          ) : (
            gruposPorDia.map(grupo => (
              <section key={format(grupo.data, 'yyyy-MM-dd')} className="flex flex-col gap-3">
                {/* Cabeçalho do grupo de dia: oculto na visão "Dia",
                    onde o título da página já informa a data */}
                {viewMode !== 'day' && (
                  <header className="flex items-center gap-3">
                    <h4 className="text-sm font-semibold capitalize text-gray-800">
                      {formatarRotuloDia(grupo.data)}
                    </h4>
                    {(isToday(grupo.data) || isTomorrow(grupo.data)) && (
                      <span className="text-xs text-gray-400">
                        {format(grupo.data, "dd 'de' MMMM", { locale: ptBR })}
                      </span>
                    )}
                    <span className="text-xs text-gray-400">
                      · {grupo.itens.length} {grupo.itens.length === 1 ? 'agendamento' : 'agendamentos'}
                    </span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </header>
                )}

                {/* "Lista" do mês usa coluna única estreita; os demais usam grade responsiva */}
                <div
                  className={cn(
                    'grid gap-4',
                    viewMode === 'month' && monthViewMode === 'list'
                      ? 'max-w-3xl grid-cols-1'
                      : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',
                  )}
                >
                  {grupo.itens.map(agendamento => (
                    <AgendamentoCard
                      key={agendamento.id}
                      agendamento={agendamento}
                      clienteNome={getClienteNome(agendamento.cliente_id)}
                      onEdit={handleEditAgendamento}
                      onDelete={handleOpenDeleteDialog}
                      onOpenChat={handleOpenChat}
                      onStatusChange={handleStatusChange}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      )}

      {/* Modal de Registro/Edição de Agendamento */}
      <RegistroAgendamentoModal
        isOpen={isAgendamentoModalOpen}
        onClose={handleCloseModal}
        agendamento={agendamentoParaEditar}
      />

      {/* Diálogo de confirmação para exclusão de agendamento */}
      <AlertDialog open={!!agendamentoParaDeletarId} onOpenChange={open => !open && setAgendamentoParaDeletarId(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso excluirá permanentemente o agendamento do sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setAgendamentoParaDeletarId(null)} className="rounded-xl">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteAgendamentoMutation.isPending}
              className="rounded-xl bg-red-600 hover:bg-red-700"
            >
              {deleteAgendamentoMutation.isPending ? 'Excluindo...' : 'Confirmar Exclusão'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de confirmação para MUDANÇA DE STATUS de agendamento */}
      <AlertDialog
        open={!!statusUpdateConfirmation}
        onOpenChange={open => {
          // Ao fechar o diálogo, limpa os estados para reabrir sempre zerado
          if (!open) {
            setStatusUpdateConfirmation(null);
            setValorConfirmado('');
            setFormaPagamentoConfirmada('');
            setParcelasConfirmadas('1');
          }
        }}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Mudança de Status</AlertDialogTitle>
            <AlertDialogDescription>
              {getStatusChangeDescription()}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Campo de valor: aparece apenas ao marcar como "Pago" */}
          {statusUpdateConfirmation?.newStatus === AgendamentoStatus.PAGO && (
            <div className="grid gap-3 py-2">
              <div className="grid gap-2">
                <Label htmlFor="valor-pago">Valor pago (R$)</Label>
                <Input
                  id="valor-pago"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Ex: 150.00"
                  value={valorConfirmado}
                  onChange={e => setValorConfirmado(e.target.value)}
                  className="mt-1 rounded-xl"
                />
                <p className="text-xs text-gray-500">
                  O valor original era R$ {statusUpdateConfirmation.agendamento.valor?.toFixed(2) || '0.00'}.
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="forma-pagamento">Como pagou</Label>
                <Select value={formaPagamentoConfirmada} onValueChange={setFormaPagamentoConfirmada}>
                  <SelectTrigger id="forma-pagamento" className="rounded-xl">
                    <SelectValue placeholder="Escolha PIX, cartão, dinheiro..." />
                  </SelectTrigger>
                  <SelectContent>
                    {FORMAS_PAGAMENTO_AGENDAMENTO.map(opcao => (
                      <SelectItem key={opcao.value} value={opcao.value}>
                        {opcao.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="parcelas-pagamento">Parcelas</Label>
                <Input
                  id="parcelas-pagamento"
                  type="number"
                  min="1"
                  step="1"
                  value={parcelasConfirmadas}
                  onChange={e => setParcelasConfirmadas(e.target.value)}
                  className="rounded-xl"
                />
                <p className="text-xs text-gray-500">À vista deixe 1. Se foi na maquininha, informe as vezes.</p>
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setStatusUpdateConfirmation(null);
                setValorConfirmado('');
                setFormaPagamentoConfirmada('');
                setParcelasConfirmadas('1');
              }}
              className="rounded-xl"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmStatusUpdate}
              disabled={
                updateAgendamentoMutation.isPending
                || (statusUpdateConfirmation?.newStatus === AgendamentoStatus.PAGO && !ehFormaPagamentoValida(formaPagamentoConfirmada))
              }
              className="rounded-xl bg-green-700 hover:bg-green-600"
            >
              {updateAgendamentoMutation.isPending ? 'Atualizando...' : 'Confirmar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
